import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import api from '../services/api'
import { useListaFlutuante } from '../hooks/useListaFlutuante'

interface ItemErp {
  its_cod: number
  its_des: string
  its_un1: string
  its_precod?: string
  its_ncm?: string
  its_cest?: string
  its_forcod?: string
  its_esp?: number
  its_srvcod?: string
  desativado?: boolean
}

interface Props {
  codValue: string
  descValue: string
  unValue: string
  onChange: (cod: string, des: string, un: string, precod: string, ncm?: string, cest?: string, forcod?: string, esp?: number, srvcod?: string) => void
  // Só itens do tipo Serviço (its_esp = 2) — usado no lançamento de NFS-e.
  somenteServico?: boolean
  // Selo Serviço/Produto em cada item da lista.
  mostrarTipo?: boolean
  // Emissão de nota: esconde/recusa item desativado (o item que já está na nota continua valendo)
  somenteAtivos?: boolean
}

function SeloTipo({ esp }: { esp?: number }) {
  const servico = esp === 2
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px', borderRadius: 8, flexShrink: 0,
      background: servico ? '#dcfce7' : '#dbeafe', color: servico ? '#166534' : '#1e40af',
    }}>{servico ? 'Serviço' : 'Produto'}</span>
  )
}

export default function ItemSelect({ codValue, descValue, unValue, onChange, somenteServico, mostrarTipo, somenteAtivos }: Props) {
  const [aberta, setAberta] = useState(false)
  const [filtro, setFiltro] = useState('')
  const [resultados, setResultados] = useState<ItemErp[]>([])
  const [buscando, setBuscando] = useState(false)
  const [localCod, setLocalCod] = useState(codValue)
  const [aviso, setAviso] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { posicao, listaRef } = useListaFlutuante(ref, aberta, () => setAberta(false))

  useEffect(() => { setLocalCod(codValue); if (codValue) setAviso('') }, [codValue])

  const buscar = useCallback((valor: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (valor.length < 2) { setResultados([]); return }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const r = await api.get('/erp/itens', { params: { busca: valor, somenteServico: somenteServico ? 'S' : undefined, ativos: somenteAtivos ? '1' : undefined } })
        setResultados(r.data)
      } finally {
        setBuscando(false)
      }
    }, 350)
  }, [somenteServico, somenteAtivos])

  async function buscarPorCodigo(cod: string) {
    const num = cod.replace(/\D/g, '')
    if (!num) { onChange('', '', unValue, ''); setAviso(''); return }
    try {
      const r = await api.get(`/erp/itens/${num}`)
      if (r.data && somenteServico && r.data.its_esp !== 2) {
        onChange('', '', unValue, '')
        setAviso(`Item "${num}" é Produto — na NFS-e só item do tipo Serviço`)
      } else if (r.data && somenteAtivos && r.data.desativado && num !== codValue) {
        onChange('', '', unValue, '')
        setAviso(`Item "${num}" está desativado no cadastro de itens`)
      } else if (r.data) {
        onChange(String(r.data.its_cod), r.data.its_des, r.data.its_un1 || unValue, r.data.its_precod || '', r.data.its_ncm || '', r.data.its_cest || '', r.data.its_forcod || '', r.data.its_esp, r.data.its_srvcod || '')
        setLocalCod(String(r.data.its_cod))
        setAviso('')
      } else {
        onChange('', '', unValue, '')
        setAviso(`Código "${num}" não encontrado`)
      }
    } catch {
      onChange('', '', unValue, '')
      setAviso(`Código "${num}" não encontrado`)
    }
  }

  // A lista fica num portal no fim do body: Tab ali pularia pro início da página (sidebar).
  // Volta o foco pro formulário, no campo seguinte ao componente (ou no próprio código).
  function focarDepois(proximo: boolean) {
    const wrap = ref.current
    if (!wrap) return
    const campos = Array.from(document.querySelectorAll<HTMLElement>('input, select, textarea, button, [tabindex]:not([tabindex="-1"])'))
      .filter(el => !(el as HTMLInputElement).disabled && el.offsetParent !== null && !listaRef.current?.contains(el))
    if (proximo) {
      const seguinte = campos.find(el => !wrap.contains(el) && (wrap.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING))
      if (seguinte) { seguinte.focus(); return }
    }
    wrap.querySelector<HTMLInputElement>('.nat-cod-input')?.focus()
  }

  function teclaFiltro(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault()
      if (resultados[0]) selecionar(resultados[0])
      else setAberta(false)
      setTimeout(() => focarDepois(!e.shiftKey), 0)
    } else if (e.key === 'Escape') {
      setAberta(false)
      setTimeout(() => focarDepois(false), 0)
    }
  }

  function selecionar(item: ItemErp) {
    onChange(String(item.its_cod), item.its_des, item.its_un1 || 'UN', item.its_precod || '', item.its_ncm || '', item.its_cest || '', item.its_forcod || '', item.its_esp, item.its_srvcod || '')
    setLocalCod(String(item.its_cod))
    setAviso('')
    setAberta(false)
    setFiltro('')
    setResultados([])
  }

  return (
    <div className="nat-wrap" ref={ref}>
      <div className="nat-input-row">
        <input
          className="nat-cod-input"
          type="text"
          placeholder="Cód."
          value={localCod}
          style={aviso ? { borderColor: '#dc2626' } : undefined}
          onChange={e => { setLocalCod(e.target.value.replace(/\D/g, '')); setAviso('') }}
          onBlur={e => {
            if (e.target.value) buscarPorCodigo(e.target.value)
            else { onChange('', '', unValue, ''); setAviso('') }
          }}
          onKeyDown={e => { if (e.key === 'Enter') buscarPorCodigo(localCod) }}
        />
        <div className="nat-desc-box" onClick={() => { setAberta(o => !o); setFiltro(''); setResultados([]) }}>
          {descValue ? (
            <>
              <span className="nat-dsc">{descValue}</span>
              {unValue && <span className="nat-cfop" style={{ flexShrink: 0 }}>{unValue}</span>}
            </>
          ) : (
            <span className="nat-placeholder">Digite o código ou clique para buscar...</span>
          )}
          <span className="nat-chevron">{aberta ? '▴' : '▾'}</span>
        </div>
      </div>
      {aviso && <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.2rem', paddingLeft: '0.25rem' }}>{aviso}</div>}

      {aberta && posicao && createPortal(
        <div ref={listaRef} className="nat-lista" style={{ position: 'fixed', ...posicao, minWidth: posicao.width }}>
          <div className="nat-filtro-wrap">
            <input
              autoFocus
              className="nat-filtro"
              placeholder="Código ou descrição..."
              value={filtro}
              onChange={e => { setFiltro(e.target.value); buscar(e.target.value) }}
              onKeyDown={teclaFiltro}
            />
          </div>
          <div className="nat-itens">
            {buscando && <div className="nat-vazio">Buscando...</div>}
            {!buscando && filtro.length >= 2 && resultados.length === 0 && (
              <div className="nat-vazio">Nenhum resultado</div>
            )}
            {!buscando && filtro.length < 2 && (
              <div className="nat-vazio" style={{ color: '#9ca3af' }}>Digite ao menos 2 caracteres para buscar</div>
            )}
            {resultados.map(item => (
              <button
                key={item.its_cod}
                className={`nat-item${String(item.its_cod) === codValue ? ' nat-item--ativo' : ''}`}
                onClick={() => selecionar(item)}
              >
                <div className="nat-item-linha1">
                  <span className="nat-item-cod">#{item.its_cod}</span>
                  <span className="nat-item-cfop">{item.its_un1}</span>
                  {mostrarTipo && <SeloTipo esp={item.its_esp} />}
                </div>
                <div className="nat-item-dsc">{item.its_des}</div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
